/**
 * Scoped role types for the multi-business/branch membership model.
 *
 * GlobalRole:   SuperAdmin — set via Firebase Auth custom claim, not Firestore.
 * BusinessRole: Only 'BUSINESS' lives at the business-member level.
 * BranchRole:   All three roles appear in branch memberships.
 *               BUSINESS members are auto-seeded into every branch by the callable.
 */

export type GlobalRole = 'SUPER_ADMIN';
export type BusinessRole = 'BUSINESS';
export type BranchRole = 'BUSINESS' | 'OWNER' | 'MANAGER';

export const GLOBAL_ROLES = { SUPER_ADMIN: 'SUPER_ADMIN' } as const;
export const BUSINESS_ROLES = { BUSINESS: 'BUSINESS' } as const;
export const BRANCH_ROLES = {
  BUSINESS: 'BUSINESS',
  OWNER: 'OWNER',
  MANAGER: 'MANAGER',
} as const;

export const ROLE_LABELS: Record<GlobalRole | BranchRole, string> = {
  SUPER_ADMIN: 'Super Admin',
  BUSINESS: 'Business',
  OWNER: 'Owner',
  MANAGER: 'Manager',
};

/**
 * The caller's effective authority for an authorization decision.
 *
 * Distinct from the three role types above because those describe what is
 * *stored* in a membership document, while this describes what the caller *is*
 * for the purposes of one check — and SuperAdmin is never stored anywhere.
 *
 * Deliberately the same union in all three packages:
 *   functions/src/shared/auth.ts   → CallerAuthority
 *   task-api/src/middleware/businessAccess.ts → BusinessAuthority
 * They cannot import each other (three separate npm packages), so this file is
 * the canonical copy. See CLAUDE.md § Keep in sync.
 */
export type CallerAuthority = GlobalRole | BranchRole;

/**
 * Ordering for "at least this much authority" checks.
 *
 * MANAGER < OWNER < BUSINESS < SUPER_ADMIN. Used to collapse a user's several
 * memberships within one business down to the highest one they hold — the same
 * ranking the server applies when it derives users/{uid}/businessScopes/{bid}.
 */
export const AUTHORITY_RANK: Record<CallerAuthority, number> = {
  MANAGER: 0,
  OWNER: 1,
  BUSINESS: 2,
  SUPER_ADMIN: 3,
};

export const AUTHORITY_LABELS: Record<CallerAuthority, string> = ROLE_LABELS;

/**
 * Narrowing guard for a role value read from Firestore.
 *
 * Membership documents are written only by the Admin SDK, but a document that
 * predates a role change — or one hand-edited in the Console — can still carry
 * an unknown value. Casting it would let that value flow through as a
 * BranchRole; this rejects it instead.
 */
export function isBranchRole(value: unknown): value is BranchRole {
  return value === 'BUSINESS' || value === 'OWNER' || value === 'MANAGER';
}

export function isCallerAuthority(value: unknown): value is CallerAuthority {
  return value === 'SUPER_ADMIN' || isBranchRole(value);
}

/**
 * True when `actual` carries at least as much authority as `min`.
 *
 * A null `actual` means no standing at all and is always false, so callers can
 * pass the result of a lookup straight in without a separate null check.
 */
export function atLeast(
  actual: CallerAuthority | null,
  min: CallerAuthority,
): boolean {
  if (actual === null) return false;
  return AUTHORITY_RANK[actual] >= AUTHORITY_RANK[min];
}

/**
 * Picks the highest authority from a list, or null when the list is empty.
 */
export function highestAuthority(
  roles: readonly CallerAuthority[],
): CallerAuthority | null {
  return roles.reduce<CallerAuthority | null>(
    (best, r) => (best === null || AUTHORITY_RANK[r] > AUTHORITY_RANK[best] ? r : best),
    null,
  );
}
